import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useBrands } from '../../hooks/useApi'
import { useContentCalendar, useGenerateCalendar, useUpdateCalendar, useMarketingAccess } from '../../hooks/useMarketing'
import { Button } from '../../components/ui/Button'
import { Card, CardContent } from '../../components/ui/Card'
import { Wand2, Crown, Save } from 'lucide-react'
import { normalizePlanId } from '../../lib/plans'

type ScheduleItem = {
  day: string
  theme: string
  content: string
  productSuggestion?: string
}

export default function MarketingCalendar() {
  const { data: access } = useMarketingAccess()
  const plan = normalizePlanId(access?.plan)
  const isPremium = plan === 'premium'
  const { data: brands } = useBrands()
  const { data: calendar, refetch } = useContentCalendar(isPremium)
  const generateCalendar = useGenerateCalendar()
  const updateCalendar = useUpdateCalendar()

  const [brandId, setBrandId] = useState<number | ''>('')
  const [schedule, setSchedule] = useState<ScheduleItem[]>([])

  const displaySchedule = schedule.length > 0 ? schedule : (calendar?.schedule as ScheduleItem[]) ?? []

  if (!isPremium) {
    return (
      <div className="text-center py-16">
        <Crown className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold">Kalender Konten — Premium</h2>
        <p className="text-gray-600 mt-2 mb-4">Fitur ini tersedia untuk paket Premium</p>
        <Link to="/subscription" className="text-indigo-600 font-medium hover:underline">Upgrade ke Premium →</Link>
      </div>
    )
  }

  const handleGenerate = async () => {
    if (!brandId) return
    const res = await generateCalendar.mutateAsync({ brandId: Number(brandId) })
    setSchedule(res.calendar.schedule)
    refetch()
  }

  const handleSave = async () => {
    if (!calendar?.id && displaySchedule.length === 0) return
    const id = calendar?.id
    if (!id) return
    await updateCalendar.mutateAsync({ id, schedule: displaySchedule })
  }

  const updateItem = (index: number, field: keyof ScheduleItem, value: string) => {
    const next = [...displaySchedule]
    next[index] = { ...next[index], [field]: value }
    setSchedule(next)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Kalender Konten</h1>
        <p className="text-gray-600 mt-1">AI membuat jadwal posting mingguan (1 AI Boost)</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium mb-1">Brand</label>
              <select className="w-full border rounded-lg px-3 py-2" value={brandId} onChange={(e) => setBrandId(Number(e.target.value) || '')}>
                <option value="">Pilih Brand</option>
                {brands?.map((b: { id: number; name: string }) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <Button onClick={handleGenerate} disabled={!brandId || generateCalendar.isPending} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <Wand2 className="w-4 h-4 mr-2" />
              Generate Kalender
            </Button>
            {calendar?.id && (
              <Button variant="outline" onClick={handleSave} disabled={updateCalendar.isPending}>
                <Save className="w-4 h-4 mr-2" /> Simpan Perubahan
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {displaySchedule.length > 0 && (
        <div className="grid gap-4">
          {displaySchedule.map((item, idx) => (
            <Card key={item.day}>
              <CardContent className="pt-4">
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Hari</label>
                    <input className="w-full border rounded px-2 py-1.5 text-sm font-semibold" value={item.day} onChange={(e) => updateItem(idx, 'day', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Tema</label>
                    <input className="w-full border rounded px-2 py-1.5 text-sm" value={item.theme} onChange={(e) => updateItem(idx, 'theme', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Produk</label>
                    <input className="w-full border rounded px-2 py-1.5 text-sm" value={item.productSuggestion || ''} onChange={(e) => updateItem(idx, 'productSuggestion', e.target.value)} />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="text-xs text-gray-500">Konten</label>
                  <textarea className="w-full border rounded px-2 py-1.5 text-sm mt-1" rows={2} value={item.content} onChange={(e) => updateItem(idx, 'content', e.target.value)} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
